<template>
  <v-row justify="center">
    <v-dialog
      v-model="show"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <v-card>
        <v-toolbar dark color="primary">
          <v-btn icon dark @click="$emit('close')">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>{{ name }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn dark text @click="onSave"> Save </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-card-text v-if="isPrivate || isPublic">
          <v-row>
            <v-col cols="3">
              <v-banner single-line sticky class="components">
                <h3 class="h3">Components</h3>
                <v-row>
                  <v-col class="pb-0">
                    <v-text-field
                      label="Search"
                      :outlined="outlinedInputs"
                      dense
                      v-model="searchStr"
                      clearable
                    ></v-text-field>
                  </v-col>
                </v-row>

                <v-list nav>
                  <draggable
                    v-model="components"
                    @start="drag = true"
                    @end="drag = false"
                    :group="{ name: 'components', pull: 'clone', put: false }"
                    :clone="cloneComponent"
                  >
                    <v-list-item
                      class="elevation-1"
                      v-for="comp in components"
                      :key="comp.name"
                    >
                      <v-list-item-content>
                        <v-list-item-title>{{ comp.name }}</v-list-item-title>
                        <v-list-item-subtitle>{{
                          comp.description
                        }}</v-list-item-subtitle>
                      </v-list-item-content>

                      <v-list-item-action>
                        <v-btn icon>
                          <v-icon color="grey lighten-1">mdi-drag</v-icon>
                        </v-btn>
                      </v-list-item-action>
                    </v-list-item>
                    <v-divider inset></v-divider>
                  </draggable>
                </v-list>
              </v-banner>
            </v-col>
            <v-col>
              <v-tabs centered v-model="tabs">
                <v-tab key="details">Details</v-tab>
                <v-tab key="sections">Sections</v-tab>
                <v-tab key="preview">Preview</v-tab>
              </v-tabs>
              <v-tabs-items v-model="tabs">
                <v-tab-item key="details">
                  <v-card>
                    <v-card-text>
                      <v-row>
                        <v-col>
                          <v-text-field
                            label="Document Name"
                            v-model="name"
                            :outlined="outlinedInputs"
                          ></v-text-field>
                        </v-col>
                        <v-col sm="3">
                          <document-template-type-select
                            v-model="templateType"
                            :outlined="outlinedInputs"
                          />
                        </v-col>
                        <v-col sm="3">
                          <document-theme-list-select v-model="theme" />
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col>
                          <v-textarea
                            label="Description"
                            v-model="description"
                            :outlined="outlinedInputs"
                          />
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col>
                          <v-textarea
                            label="Custom CSS"
                            v-model="css"
                            :outlined="outlinedInputs"
                          />
                        </v-col>
                      </v-row>
                      <h3 class="h3">Visible Contents</h3>
                      <v-row>
                        <v-col>
                          <v-checkbox
                            class="pa-0 ma-0"
                            hide-details
                            label="Reports"
                            v-model="forReports"
                          ></v-checkbox>
                        </v-col>
                        <v-col>
                          <v-checkbox
                            class="pa-0 ma-0"
                            hide-details
                            label="Quotation"
                            v-model="forQuotation"
                          ></v-checkbox>
                        </v-col>
                        <v-col>
                          <v-checkbox
                            class="pa-0 ma-0"
                            hide-details
                            label="Confirmation"
                            v-model="forConfirmation"
                          ></v-checkbox>
                        </v-col>
                        <v-col>
                          <v-checkbox
                            class="pa-0 ma-0"
                            hide-details
                            label="Voucher"
                            v-model="forVoucher"
                          ></v-checkbox>
                        </v-col>
                        <v-col>
                          <v-checkbox
                            class="pa-0 ma-0"
                            hide-details
                            label="Other"
                            v-model="forOther"
                          ></v-checkbox>
                        </v-col>
                        <v-col>
                          <v-checkbox
                            class="pa-0 ma-0"
                            hide-details
                            label="Web"
                            v-model="forWeb"
                          ></v-checkbox>
                        </v-col>
                        <v-col>
                          <v-checkbox
                            class="pa-0 ma-0"
                            hide-details
                            label="App"
                            v-model="forApp"
                          ></v-checkbox>
                        </v-col>
                      </v-row>
                      <v-divider />
                      <h3 class="h3 pt-3">PDF Settings</h3>
                      <v-row align="baseline">
                        <v-col>
                          <v-select
                            v-model="orientation"
                            label="Orientation"
                            :items="orientations"
                            :outlined="outlinedInputs"
                          />
                        </v-col>
                        <v-col>
                          <v-text-field
                            :outlined="outlinedInputs"
                            type="number"
                            label="Margin Top"
                            v-model="marginTop"
                          />
                        </v-col>
                        <v-col>
                          <v-text-field
                            :outlined="outlinedInputs"
                            type="number"
                            label="Margin Right"
                            v-model="marginRight"
                          />
                        </v-col>
                        <v-col>
                          <v-text-field
                            :outlined="outlinedInputs"
                            type="number"
                            label="Margin Bottom"
                            v-model="marginBottom"
                          />
                        </v-col>
                        <v-col>
                          <v-text-field
                            :outlined="outlinedInputs"
                            type="number"
                            label="Margin Left"
                            v-model="marginLeft"
                          />
                        </v-col>
                        <v-col class="py-0">
                          <v-switch
                            v-model="skipFooter"
                            label="Hide Footer"
                          ></v-switch>
                        </v-col>
                      </v-row>
                      <v-row> </v-row>
                      <v-divider />
                      <v-row>
                        <v-col>
                          <v-checkbox
                            class="pa-0 ma-0"
                            hide-details
                            label="Use Alternative Names"
                            v-model="useAlternativeNames"
                          ></v-checkbox>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-tab-item>
                <v-tab-item key="sections">
                  <v-card
                    class="sections"
                    elevation="7"
                    color="amber lighten-5"
                  >
                    <v-card-text>
                      <draggable
                        :list="documentSections"
                        group="components"
                        @change="onDragChange"
                      >
                        <v-card flat v-if="documentSections.length == 0">
                          <v-card-text>
                            <v-row justify="center"><p>Drag Here</p></v-row>
                          </v-card-text>
                        </v-card>
                        <document-section
                          v-else
                          v-for="docSection of documentSections"
                          :key="docSection.id"
                          :section="docSection"
                          @change="onSectionChange"
                          @remove="onSectionRemove"
                        ></document-section>
                      </draggable>
                    </v-card-text>
                  </v-card>
                </v-tab-item>
                <v-tab-item key="preview">
                  <v-card>
                    <v-card-text>
                      <v-row justify="end">
                        <v-col cols="6" class="py-0 px-0">
                          <v-row>
                            <v-col class="py-0">
                              <itinerary-list-select
                                v-if="
                                  templateType == 'D' || templateType == 'V'
                                "
                                v-model="itineraryId"
                                :domain="domain"
                              ></itinerary-list-select>
                              <sales-invoice-list-select
                                v-if="templateType == 'S'"
                                v-model="salesInvoiceId"
                                :domain="domain"
                              />
                            </v-col>
                            <v-col class="py-0">
                              <v-btn @click="openPreview"
                                >Preview In new tab</v-btn
                              ></v-col
                            >
                            <v-col v-if="itineraryId || salesInvoiceId" class="py-0" cols="auto">
                              <v-btn
                                :color="showAiChat ? 'primary' : ''"
                                @click="showAiChat = !showAiChat"
                              >
                                <v-icon left>mdi-robot</v-icon>
                                AI Assistant
                              </v-btn>
                            </v-col>
                          </v-row>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col :cols="showAiChat ? 8 : 12" class="pa-0">
                          <iframe
                            :key="iframeKey"
                            :src="previewURL"
                            title="Preview"
                            width="100%"
                            class="sections"
                            border="0"
                            frameBorder="0"
                          ></iframe>
                        </v-col>
                        <v-col v-if="showAiChat" cols="4" class="pa-0 pl-2">
                          <document-ai-style-chat
                            :document-id="id"
                            :preview-url="previewURL"
                            @css-updated="css = $event"
                          />
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-tab-item>
              </v-tabs-items>
            </v-col>
          </v-row>
          <div v-if="debug">
            <code>{{ value }}</code>
          </div>
        </v-card-text>
        <v-card-text v-else class="pt-2">
          <v-alert type="warning">Not available for "old" documents.</v-alert>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import gql from "graphql-tag";
import draggable from "vuedraggable";
import { removeGQLTypename } from "../../../../helpers";
import { GT_HOSTNAME } from "../../../../constants/settings";
import DocumentSection from "./DocumentSection";
import DocumentThemeListSelect from "./DocumentThemeListSelect.vue";
import ItineraryListSelect from "./ItineraryListSelect.vue";
import DocumentTemplateTypeSelect from "./DocumtTemplateTypeSelect.vue";
import SalesInvoiceListSelect from "./SalesInvoiceListSelect.vue";
import DocumentAiStyleChat from "./DocumentAiStyleChat.vue";
const randomize = require("randomatic");
const COMPONENTS_QUERY = gql`
  query documentComponents {
    documentComponents {
      edges {
        node {
          name
          description
          group
          dir
          settings {
            settingName
            settingLabel
            settingValue
            settingValues
            settingType
            size
          }
        }
      }
    }
  }
`;
const CREATE_DOC = gql`
  mutation createAvailableDocument($input: AvailableDocumentInput!) {
    createAvailableDocument(input: $input) {
      id
      name
      description
      css
      theme
    }
  }
`;
const UPDATE_DOC = gql`
  mutation updateAvailableDocument($input: AvailableDocumentInput!) {
    updateAvailableDocument(input: $input) {
      id
      name
      description
      css
      theme
      sourceId
      isDefault
      forWeb
      forReports
      forQuotation
      forConfirmation
      forVoucher
      forOther
      forApp
      useAlternativeNames
      marginTop
      marginBottom
      marginRight
      marginLeft
      orientation
      skipFooter
    }
  }
`;
const DOC_DETAILS = gql`
  query docDetails($id: ID!) {
    availableDocument(id: $id) {
      id
      name
      description
      css
      theme
      sourceId
      isDefault
      forWeb
      forReports
      forQuotation
      forConfirmation
      forVoucher
      forOther
      forApp
      useAlternativeNames
      marginTop
      marginBottom
      marginRight
      marginLeft
      orientation
      type
      skipFooter
      sections {
        edges {
          node {
            id
            name
            description
            dir
            settings {
              settingLabel
              settingName
              settingValue
              settingValues
              settingType
              size
            }
          }
        }
      }
    }
  }
`;
export default {
  name: "document-builder",

  props: {
    show: {
      type: Boolean,
      default: () => false,
    },
    isPublic: {
      type: Boolean,
      default: () => false,
    },
    value: {
      type: Object,
      default: () => null,
    },
    domain: {
      type: String,
      default: () => "",
    },
  },

  components: {
    draggable,
    DocumentSection,
    DocumentThemeListSelect,
    ItineraryListSelect,
    DocumentTemplateTypeSelect,
    SalesInvoiceListSelect,
    DocumentAiStyleChat,
  },

  data() {
    return {
      debug: false,
      searchStr: "",
      tabs: [],
      showAiChat: false,
      iframeKey: 0,
      id: this.value ? this.value.id : "",
      name: this.value ? this.value.name : "Document Name",
      css: this.value ? this.value.css : "/* custom css */",
      theme: this.value ? this.value.theme : "empty",
      forWeb: this.value && this.value.forWeb ? this.value.forWeb : false,
      forReports:
        this.value && this.value.forReports ? this.value.forReports : false,
      forQuotation:
        this.value && this.value.forQuotation ? this.value.forQuotation : false,
      forConfirmation:
        this.value && this.value.forConfirmation
          ? this.value.forConfirmation
          : false,
      forVoucher:
        this.value && this.value.forVoucher ? this.value.forVoucher : false,
      forOther: this.value && this.value.forOther ? this.value.forOther : false,
      forApp: this.value && this.value.forApp ? this.value.forApp : false,
      isDefault:
        this.value && this.value.isDefault ? this.value.isDefault : false,
      useAlternativeNames:
        this.value && this.value.useAlternativeNames
          ? this.value.useAlternativeNames
          : false,
      description: this.value
        ? this.value.descriptions
        : "Document Description",
      documentSections: [],
      itineraryId: "",
      salesInvoiceId: "",
      purchaseInvoiceId: "",
      templateType: this.value ? this.value.type : "D",
      marginTop: this.value ? this.value.marginTop : 10,
      marginBottom: this.value ? this.value.marginBottom : 10,
      marginLeft: this.value ? this.value.marginLeft : 10,
      marginRight: this.value ? this.value.marginRight : 10,
      orientation: this.value ? this.value.orientation : "Portrait",
      orientations: ["Portrait", "Landscape"],
      skipFooter: this.value ? this.value.skipFooter : false,
    };
  },

  computed: {
    isPrivate() {
      return (
        !this.isPublic &&
        this.availableDocument &&
        this.availableDocument.sourceId != ""
      );
    },
    previewURL() {
      switch (this.templateType) {
        case "D":
          return `${GT_HOSTNAME}/report/document_preview/${this.id}?itinerary_id=${this.itineraryId}`;
          break;
        case "S":
          return `${GT_HOSTNAME}/report/document_preview/${this.id}?invoice_id=${this.salesInvoiceId}`;
          break;
        default:
          return `${GT_HOSTNAME}/report/document_preview/${this.id}?itinerary_id=${this.itineraryId}`;
      }
    },
    document() {
      return {
        id: this.id != "" ? this.id : undefined,
        name: this.name,
        description: this.description,
        css: this.css,
        theme: this.theme,
        sections: this.documentSections.map(this.mapDocumentSectionInput),
        forQuotation: this.forQuotation,
        forConfirmation: this.forConfirmation,
        forReports: this.forReports,
        forApp: this.forApp,
        forVoucher: this.forVoucher,
        forOther: this.forOther,
        isDefault: this.isDefault,
        useAlternativeNames: this.useAlternativeNames,
        templateType: this.templateType,
        marginTop: parseInt(this.marginTop),
        marginBottom: parseInt(this.marginBottom),
        marginLeft: parseInt(this.marginLeft),
        marginRight: parseInt(this.marginRight),
        orientation: this.orientation,
        skipFooter: this.skipFooter,
      };
    },
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    components() {
      const dcs =
        this.documentComponents && this.documentComponents.edges
          ? this.documentComponents.edges
              .map(removeGQLTypename)
              .filter((d) => d.group == this.templateType)
              .filter(this.searchFilter)
          : [];

      if (this.isPrivate && !this.$store.getters.userConfig.isSupervisor) {
        const currentDocComponents = this.availableDocument
          ? this.availableDocument.sections.edges.map((s) => s.node.dir)
          : [];

        return dcs
          .filter((dc) => currentDocComponents.indexOf(dc.dir) > -1)
          .filter(this.searchFilter);
      } else {
        return dcs;
      }
    },
  },

  watch: {
    value(newVal) {
      this.name = newVal ? newVal.name : "Document Name";
      this.description = newVal ? newVal.description : "Document Description";
      this.id = newVal ? newVal.id : "";
      this.templateType = newVal ? newVal.type : "D";
      this.marginTop = newVal && newVal.marginTop ? newVal.marginTop : 10;
      this.marginBottom =
        newVal && newVal.marginBottom ? newVal.marginBottom : 10;
      this.marginLeft = newVal && newVal.marginLeft ? newVal.marginLeft : 10;
      this.marginRight = newVal && newVal.marginRight ? newVal.marginRight : 10;
      this.orientation = newVal ? newVal.orientation : "Portrait";
      this.forQuotation =
        newVal && newVal.forQuotation
          ? newVal.forQuotation.toString() == "true"
          : false;
      this.forConfirmation =
        newVal && newVal.forConfirmation
          ? newVal.forConfirmation.toString() == "true"
          : false;
      this.forReports =
        newVal && newVal.forReports
          ? newVal.forReports.toString() == "true"
          : false;
      this.forApp =
        newVal && newVal.forApp ? newVal.forApp.toString() == "true" : false;
      this.forVoucher =
        newVal && newVal.forVoucher
          ? newVal.forVoucher.toString() == "true"
          : false;
      this.forOther =
        newVal && newVal.forOther
          ? newVal.forOther.toString() == "true"
          : false;
      this.isDefault =
        newVal && newVal.isDefault
          ? newVal.isDefault.toString() == "true"
          : false;
      this.useAlternativeNames =
        newVal && newVal.useAlternativeNames
          ? newVal.useAlternativeNames.toString() == "true"
          : false;
      this.skipFooter =
        newVal && newVal.skipFooter
          ? newVal.skipFooter.toString() == "true"
          : false;
      this.css = newVal ? newVal.css : "/* custom css */";
      this.theme = newVal ? newVal.theme : "empty";
      //reset sections
      if (!newVal) {
        this.documentSections = [];
      }
    },
  },

  mounted() {
    this.$root.$on("refresh-preview", () => {
      this.iframeKey++;
    });
  },

  beforeDestroy() {
    this.$root.$off("refresh-preview");
  },

  methods: {
    searchFilter(item) {
      return this.searchStr && this.searchStr != ""
        ? item.name.toLowerCase().indexOf(this.searchStr.toLowerCase()) > -1
        : true;
    },
    openPreview() {
      window.open(this.previewURL, "_blank");
    },
    onSectionChange(payload) {
      const editedIndex = this.documentSections.findIndex(
        (d) => d.id == payload.id
      );
      Object.assign(this.documentSections[editedIndex], { ...payload });
      //console.log("on section change", payload);
    },
    onSectionRemove(payload) {
      this.documentSections = this.documentSections.filter(
        (s) => s.id != payload.id
      );
    },
    mapDocumentSectionInput(item) {
      return {
        name: item.name,
        dir: item.dir,
        description: item.description,
        settings: item.settings.map(this.mapSectionSettingInput),
      };
    },
    mapSectionSettingInput(item) {
      return {
        settingName: item.settingName,
        settingLabel: item.settingLabel,
        settingValue: item.settingValue,
        settingValues: item.settingValues,
        settingType: item.settingType,
        size: item.size,
      };
    },
    onDragChange(payload) {
      console.log("drag change", payload);
    },
    cloneComponent(payload) {
      console.log("on clone", payload);
      return {
        id: randomize("Aa0", 32),
        ...payload,
      };
    },
    onSave() {
      //this.showDialog = false;
      this.$apollo
        .mutate({
          mutation: this.id == "" ? CREATE_DOC : UPDATE_DOC,
          variables: { input: this.document },
        })
        .then((res) => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Document Saved.",
          });
          this.$emit("saved", res.data);
        })
        .catch((err) => {
          const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
        });
    },
  },

  apollo: {
    documentComponents: {
      query: COMPONENTS_QUERY,
    },
    availableDocument: {
      query: DOC_DETAILS,
      variables() {
        return { id: this.id };
      },
      skip() {
        return this.id == "";
      },
      fetchPolicy: "network-only",
      result({ data }) {
        console.log(data.availableDocument);
        this.name = data.availableDocument.name;
        this.documentSections =
          data.availableDocument.sections.edges.map(removeGQLTypename);
        this.description = data.availableDocument.description;
        this.css = data.availableDocument.css;
        this.theme = data.availableDocument.theme;
        this.forQuotation =
          data.availableDocument.forQuotation.toString() == "true";
        this.forConfirmation =
          data.availableDocument.forConfirmation.toString() == "true";
        this.forReports =
          data.availableDocument.forReports.toString() == "true";
        this.forApp = data.availableDocument.forApp.toString() == "true";
        this.forVoucher =
          data.availableDocument.forVoucher.toString() == "true";
        this.forOther = data.availableDocument.forOther.toString() == "true";
        this.isDefault = data.availableDocument.isDefault.toString() == "true";
        this.useAlternativeNames =
          data.availableDocument.useAlternativeNames.toString() == "true";
        this.marginTop = data.availableDocument.marginTop;
        this.marginBottom = data.availableDocument.marginBottom;
        this.marginLeft = data.availableDocument.marginLeft;
        this.marginRight = data.availableDocument.marginRight;
        this.orientation = data.availableDocument.orientation;
        this.templateType = data.availableDocument.type;
        this.skipFooter =
          data.availableDocument.skipFooter.toString() == "true";
      },
    },
  },
};
</script>

<style>
.sections {
  min-height: calc(100vh - 140px);
}
.components {
  max-height: calc(100vh - 80px);
  overflow: scroll;
  position: relative;
  top: 180px;
}
</style>



// WEBPACK FOOTER //
// src/components/gt/admin/documents/DocumentBuilder.vue